import { ModuleConfig } from "../../types/ModuleConfig";

/**
 * Authentication Module Config
 *
 * This module is consumed via `./index.ts` exports (UnifiedLoginModal,
 * SecureMembershipButton, AuthContext queries, etc.) rather than by
 * registering routes/nav. The moduleConfig exists only so the cross-tenant
 * drift scanner can read a version string and track this module alongside
 * the route-based modules on the Module Upgrade Matrix.
 *
 * Integration guide: see ./CLAUDE.md
 */
const authenticationModuleConfig: ModuleConfig = {
    id: "authentication",
    name: "Authentication",
    description: "Unified login + SMS/email verification + username/password + myClient AuthContext",
    version: "1.5.0", // Where you're signed in: each sign-in listed with its own Sign out.
    enabled: true,
    category: "core",
    order: 0,
    icon: "🔐",
    navigation: [],
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },
    routes: [],
    changelog: [
        {
            version: "1.5.0",
            releaseDate: "2026-09-28",
            title: "Where you're signed in",
            features: [
                "Security page: a Where you're signed in section — every phone, tablet and computer signed in to your account on this site, most recently active first, with the device (\"Chrome on Mac\"), its IP address, when it signed in and when it was last active; this device is marked",
                "A Sign out button on each one (in-page confirm; only the server's confirmed time is shown). Signing out this device takes you to sign in",
                "Sign out everywhere now also clears the list of every other device",
            ],
            notes: "Needs a backend with mySessions and revokeMySession (sign-in records with a sid claim). Sign-ins from before the backend deploy aren't listed; Sign out everywhere still ends them.",
        },
        {
            version: "1.4.0",
            releaseDate: "2026-09-28",
            title: "Sign out everywhere",
            features: [
                "Security page: a Sign out everywhere section — signs out every other phone, tablet and computer; this device stays signed in (in-page confirm, then the server's time is shown)",
                "Change password now signs out every other device too, and says so before you save (changeMyPassword; the returned token is adopted)",
                "Reset password from the Security page: every device is signed out, including this one, and you are taken to sign in",
            ],
            notes: "Needs a backend with signOutEverywhere, changeMyPassword and myTwoFactorStatus.sessionsValidAfter. The legacy changePassword mutation still works but does not sign anyone out.",
        },
        {
            version: "1.3.0",
            releaseDate: "2026-09-26",
            title: "Two-step verification (step-up)",
            features: [
                "Security page: a Two-step verification card — status in words, the sign-in methods you have set up, Turn on (only when two different methods are set up), Turn off, New recovery codes",
                "Recovery codes shown once, with Copy, Download .txt and \"I've saved them\"",
                "\"Confirm it's you\" step-up dialog: passkey, password, email code, text code or a recovery code",
                "useStepUp() hook and RequireTwoFactor page wrapper for future secure pages",
                "AuthContext (shared file) gains assurance + adoptToken — additive, the existing API is unchanged",
                "Profile Settings: the old two-factor switch (never enforced) is replaced by a link to the Security page",
            ],
            notes: "Needs a backend with myTwoFactorStatus and the step-up mutations. Sign-in itself is unchanged; nothing is forced.",
        },
    ],
};

export default authenticationModuleConfig;
